<script setup lang="ts">
import { computed } from 'vue'
import { useOrgStore } from '@/stores/org'
import { useUserStore } from '@/stores/user'
import { useMapsStore } from '@/stores/maps'
import { Trash2 } from 'lucide-vue-next'

import { MemberAvatar } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { MAP_ROLES } from '@/components/maps/mapRoles'

const props = defineProps<{
  members: any[]
  mapId: string
}>()

const orgStore = useOrgStore()

const userStore = useUserStore()

const mapsStore = useMapsStore()
const orgId = computed(() => orgStore.selectedOrg as string)
const currentMemberId = computed(() => userStore.member?.member_id)

const isSelf = (m: any) => m.member_id === currentMemberId.value

const displayName = (m: any) =>
  m.display_name || `${m.first_name || ''} ${m.last_name || ''}`.trim() || m.email || 'Unknown'

const updateRole = (m: any, role: string) => {
  if (isSelf(m) || role === m.role_name) return
  mapsStore.updateMapMember({
    orgId: orgId.value,
    mapId: props.mapId,
    mapMemberId: m.map_member_id,
    role_name: role,
  })
}

const remove = (m: any) => {
  if (isSelf(m)) return
  mapsStore.deleteMapMember({
    orgId: orgId.value,
    mapId: props.mapId,
    mapMemberId: m.map_member_id,
  })
}
</script>

<template>
  <div class="divide-y divide-border" data-testid="map-members-list">
    <div
      v-for="m in members"
      :key="m.map_member_id"
      class="flex items-center gap-3 py-2.5"
    >
      <MemberAvatar :avatar_url="m.avatar_url" :verified="m.verified" />
      <div class="min-w-0 flex-1">
        <p class="text-sm font-medium truncate">{{ displayName(m) }}</p>
        <p class="text-xs text-muted-foreground truncate">{{ m.email }}</p>
      </div>
      <Tabs
        :model-value="m.role_name"
        :class="{ 'pointer-events-none opacity-60': isSelf(m) }"
        @update:model-value="v => updateRole(m, String(v))"
      >
        <TabsList class="h-6 p-0.5">
          <TabsTrigger
            v-for="r in MAP_ROLES"
            :key="r.value"
            :value="r.value"
            :disabled="isSelf(m)"
            class="h-5 px-1.5 text-xs"
          >{{ r.label }}</TabsTrigger>
        </TabsList>
      </Tabs>
      <Button
        v-if="!isSelf(m)"
        variant="ghost"
        size="sm"
        class="h-7 w-7 p-0 shrink-0"
        @click="remove(m)"
      >
        <Trash2 class="h-4 w-4 text-red-400" />
      </Button>
      <div v-else class="h-7 w-7 shrink-0" aria-hidden="true"></div>
    </div>
  </div>
</template>
